<app-page-wrapper class="grocery-page-wrapper" [title]="'glossGrocery' | i18n">
  <button
    *ngIf="groceryPluginInstalled()"
    type="button"
    title-extra
    class="tw-inline-flex tw-items-center tw-justify-center tw-border-none tw-bg-transparent tw-p-0 tw-cursor-pointer tw-text-color-icon-default hover:tw-text-color-icon-action"
    [attr.aria-label]="'groceryHelpAria' | i18n"
    [attr.title]="'groceryHelpAria' | i18n"
    (click)="openHelpDialog()"
  >
    <icon name="info-tooltip" [size]="5"></icon>
  </button>

  <!-- Mobile-only quick action: toggle Archived Lists — same action as the one inside the card
       below. Projected as another title-extra element (not nav-action) so it's guaranteed to
       render right next to "Grocery" in the same flex row as the title, on every breakpoint —
       nav-action instead renders far right via tw-ml-auto, which isn't "next to" the title. -->
  <button
    *ngIf="groceryPluginInstalled()"
    title-extra
    type="button"
    id="grocery-archived-header-toggle"
    class="tw-hidden sm:tw-flex tw-ml-auto tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-border-none tw-bg-color-surface-input tw-rounded-full tw-p-2 tw-shadow-elevation-card tw-cursor-pointer tw-text-color-icon-action"
    [attr.aria-label]="'groceryArchivedLists' | i18n"
    [attr.aria-pressed]="showPastLists()"
    [attr.title]="'groceryArchivedLists' | i18n"
    (click)="togglePastLists()"
  >
    <icon name="archive" [size]="6" currentColor="color-icon-action"></icon>
  </button>

  @if (groceryPluginInstalled()) {
    <div
      class="tw-grid tw-grid-cols-5 tw-gap-5 sm:tw-gap-2 tw-items-start tw-w-full tw-max-w-6xl sm:tw-grid-cols-1"
    >
      @if (showPastLists()) {
        <!-- Card · Archived Lists -->
        <div
          class="tw-col-span-3 sm:tw-col-span-1 tw-flex tw-flex-col tw-bg-color-surface-card tw-rounded-2xl tw-p-4 tw-shadow-elevation-card tw-gap-3.5"
        >
          <div class="tw-flex tw-flex-col tw-gap-1">
            <div class="tw-flex tw-items-center tw-gap-5">
              <h2
                id="grocery-archived-heading"
                tabindex="-1"
                class="tw-text-base tw-font-semibold tw-text-color-text-default tw-m-0 focus:tw-outline-none"
              >
                {{ "groceryArchivedLists" | i18n }}
              </h2>
              <span class="tw-flex-1"></span>
              <button
                type="button"
                class="tw-flex sm:tw-hidden tw-items-center tw-gap-1 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-[13px] tw-font-semibold tw-text-color-text-action"
                (click)="togglePastLists()"
              >
                <icon
                  name="chevron-down"
                  [size]="5"
                  currentColor="color-icon-action"
                  class="tw-rotate-90"
                ></icon>
                {{ "groceryBuildList" | i18n }}
              </button>
              <!-- Mobile-only collapse toggle — same pattern as the "My Grocery List" and
                   "Price Comparison" cards (see mobileListExpanded / mobilePriceComparisonExpanded). -->
              <button
                type="button"
                class="tw-hidden sm:tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0.5"
                (click)="mobileArchivedListsExpanded.set(!mobileArchivedListsExpanded())"
                [attr.aria-expanded]="mobileArchivedListsExpanded()"
                [attr.aria-label]="'groceryToggleArchivedListsCardAria' | i18n"
              >
                @if (mobileArchivedListsExpanded()) {
                  <icon
                    name="chevrons-collapse"
                    [size]="5"
                    currentColor="color-icon-default"
                  ></icon>
                } @else {
                  <icon name="chevrons-expand" [size]="5" currentColor="color-icon-default"></icon>
                }
              </button>
            </div>
            <!-- Mobile-only: "My Grocery List" back-link moves to its own second line, matching
                 the Figma mobile layout (desktop keeps it inline with the title above). -->
            <button
              type="button"
              class="tw-hidden sm:tw-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-[13px] tw-font-semibold tw-text-color-text-action tw-self-start"
              (click)="togglePastLists()"
            >
              <icon
                name="chevron-down"
                [size]="5"
                currentColor="color-icon-action"
                class="tw-rotate-90"
              ></icon>
              {{ "groceryBuildList" | i18n }}
            </button>
            <p class="tw-text-xs tw-text-color-text-tertiary tw-m-0">
              {{ "groceryArchivedListsHint" | i18n }}
            </p>
          </div>

          @if (pastListsStatus() === "loading") {
            <p
              class="tw-text-sm tw-text-color-text-tertiary tw-py-1 tw-m-0"
              [ngClass]="mobileArchivedListsExpanded() ? '' : 'sm:tw-hidden'"
            >
              {{ "groceryPastListsLoading" | i18n }}
            </p>
          } @else if (pastListsStatus() === "error") {
            <p
              class="tw-text-sm tw-text-color-text-tertiary tw-py-1 tw-m-0"
              [ngClass]="mobileArchivedListsExpanded() ? '' : 'sm:tw-hidden'"
            >
              {{ "groceryPastListsError" | i18n }}
            </p>
          } @else if (pastLists().length === 0) {
            <p
              class="tw-text-sm tw-text-color-text-tertiary tw-py-1 tw-m-0"
              [ngClass]="mobileArchivedListsExpanded() ? '' : 'sm:tw-hidden'"
            >
              {{ "groceryNoPastLists" | i18n }}
            </p>
          } @else {
            <div
              class="tw-flex tw-flex-col tw-gap-2"
              [ngClass]="mobileArchivedListsExpanded() ? '' : 'sm:tw-hidden'"
            >
              @for (row of pastListRows(); track row.list._id) {
                <div
                  class="tw-bg-color-surface-sunken tw-border tw-border-color-border-subtle tw-rounded-xl tw-px-3.5 tw-py-3 tw-flex tw-flex-col tw-gap-2.5"
                >
                  <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2.5">
                    <button
                      type="button"
                      class="tw-flex tw-items-center tw-gap-1.5 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-left"
                      (click)="togglePastListItems(row.list)"
                      [attr.aria-expanded]="expandedPastListId() === row.list._id"
                    >
                      <span class="tw-text-color-text-tertiary tw-text-xs tw-shrink-0">
                        {{ expandedPastListId() === row.list._id ? "▾" : "▸" }}
                      </span>
                      <span
                        class="tw-text-[13px] tw-font-semibold tw-text-color-text-default tw-whitespace-nowrap sm:tw-whitespace-normal"
                      >
                        {{ row.label }}
                      </span>
                    </button>
                    <span
                      class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-full tw-bg-color-surface-card tw-border tw-border-color-border-subtle tw-text-color-text-secondary tw-text-[13px] tw-font-medium tw-whitespace-nowrap sm:tw-hidden"
                    >
                      {{ "groceryArchiveSummaryChip" | i18n: row.itemCount : row.tickedCount }}
                    </span>
                    <span class="tw-flex-1"></span>
                    <p
                      class="tw-text-sm tw-font-semibold tw-text-color-text-default tw-m-0 tw-whitespace-nowrap"
                    >
                      {{ row.totalLabel }}
                    </p>
                    <button
                      type="button"
                      class="tw-flex tw-items-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-xs tw-font-semibold tw-text-color-text-action tw-whitespace-nowrap disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                      [disabled]="pastCopyBusy() || pastCopyUnavailable()"
                      (click)="copyPastList(row.list)"
                    >
                      {{ "groceryCopyToList" | i18n }}
                    </button>
                    <button
                      type="button"
                      class="tw-flex tw-items-center tw-gap-1.5 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-color-text-tertiary hover:tw-text-color-status-error-text disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                      [disabled]="deletingPastListId() !== null"
                      [attr.aria-label]="'groceryDeleteList' | i18n"
                      (click)="deletePastList(row.list)"
                    >
                      <icon name="trash-bin" [size]="5" currentColor="color-icon-secondary"></icon>
                      <span class="tw-text-xs tw-font-semibold tw-whitespace-nowrap sm:tw-hidden">{{
                        "groceryDeleteList" | i18n
                      }}</span>
                    </button>
                    <button
                      type="button"
                      class="tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-2 tw--m-2"
                      (click)="togglePastListItems(row.list)"
                      [attr.aria-expanded]="expandedPastListId() === row.list._id"
                      [attr.aria-controls]="
                        expandedPastListId() === row.list._id
                          ? 'past-list-items-' + row.list._id
                          : null
                      "
                      [attr.aria-label]="'groceryToggleArchivedListAria' | i18n: row.label"
                    >
                      <icon
                        name="chevron-down"
                        [size]="5"
                        currentColor="color-icon-secondary"
                        [ngClass]="expandedPastListId() === row.list._id ? 'tw-rotate-180' : ''"
                      ></icon>
                    </button>
                  </div>

                  @if (expandedPastListId() === row.list._id) {
                    <div
                      class="tw-flex tw-flex-col tw-gap-2"
                      [id]="'past-list-items-' + row.list._id"
                    >
                      <ul class="tw-list-none tw-m-0 tw-p-0 tw-flex tw-flex-col tw-gap-2">
                        @for (item of row.list.items; track item._id) {
                          @let onList = pastItemOnList(item);
                          <li
                            class="tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap sm:tw-flex-nowrap tw-w-full"
                          >
                            <p
                              class="tw-text-[13px] tw-font-medium tw-text-color-text-default tw-m-0 tw-min-w-0 tw-break-words sm:tw-flex-1 sm:tw-truncate"
                              [attr.title]="item.name"
                            >
                              {{ item.name }}
                            </p>
                            @if (!item.purchased) {
                              <span
                                class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-full tw-bg-color-status-warning-bg tw-text-color-status-warning-text tw-text-[13px] tw-font-medium tw-whitespace-nowrap tw-shrink-0"
                              >
                                {{ "groceryWasUnticked" | i18n }}
                              </span>
                            }
                            <span class="tw-flex-1 sm:tw-hidden"></span>
                            @if (pastItemCheapest(item); as cheapest) {
                              <p
                                class="tw-text-xs tw-text-color-text-tertiary tw-m-0 tw-whitespace-nowrap sm:tw-hidden"
                              >
                                {{ cheapest.label }} {{ formatPrice(cheapest.price) }}
                                @if (pastItemPriceAge(cheapest); as age) {
                                  · {{ "groceryPricedAgo" | i18n: age }}
                                }
                              </p>
                            }
                            @if (onList) {
                              <span
                                class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-full tw-bg-color-status-success-bg tw-text-color-status-success-text tw-text-[13px] tw-font-medium tw-whitespace-nowrap tw-shrink-0"
                              >
                                {{ "groceryAlreadyOnList" | i18n }}
                              </span>
                            } @else {
                              <button
                                type="button"
                                class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-lg tw-bg-color-surface-card tw-text-color-text-action tw-text-[13px] tw-font-medium tw-font-montserrat tw-leading-none tw-tracking-normal tw-shadow-elevation-card tw-border-none tw-cursor-pointer tw-shrink-0 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                [disabled]="pastCopyBusy() || pastCopyUnavailable()"
                                [attr.aria-label]="'groceryAddPastItemAria' | i18n: item.name"
                                (click)="addPastItem(item)"
                              >
                                {{ "groceryAdd" | i18n }}
                              </button>
                            }
                          </li>
                        }
                      </ul>
                      @let addableCount = pastListAddableItems(row.list).length;
                      <button
                        type="button"
                        class="tw-text-xs tw-font-semibold tw-text-color-text-action tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-left disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        [disabled]="addableCount === 0 || pastCopyBusy() || pastCopyUnavailable()"
                        (click)="copyPastList(row.list)"
                      >
                        @if (addableCount === 0) {
                          {{ "groceryAllAlreadyOnList" | i18n }}
                        } @else {
                          {{ "groceryAddAllToCurrentList" | i18n: addableCount }}
                        }
                      </button>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>

        <!-- Card · Current list -->
        <div
          class="tw-col-span-2 sm:tw-col-span-1 tw-flex tw-flex-col tw-bg-color-surface-card tw-rounded-2xl tw-p-4 tw-shadow-elevation-card tw-gap-3.5"
        >
          <div class="tw-flex tw-flex-col tw-gap-1">
            <h2 class="tw-text-base tw-font-semibold tw-text-color-text-default tw-m-0">
              {{ "groceryCurrentListTitle" | i18n }}
            </h2>
            <p class="tw-text-xs tw-text-color-text-tertiary tw-m-0">
              {{
                listRows().length === 1
                  ? ("groceryCurrentListHintSingle" | i18n)
                  : ("groceryCurrentListHint" | i18n: listRows().length)
              }}
            </p>
          </div>

          @if (listStatus() === "loading") {
            <p class="tw-text-sm tw-text-color-text-tertiary tw-py-2 tw-m-0">
              {{ "groceryListLoading" | i18n }}
            </p>
          } @else if (listStatus() === "error") {
            <p class="tw-text-sm tw-text-color-text-tertiary tw-py-2 tw-m-0">
              {{ "groceryListError" | i18n }}
            </p>
          } @else if (listRows().length === 0) {
            <app-empty-state
              titleKey="groceryCurrentListEmptyTitle"
              descriptionKey="groceryCurrentListEmptyDescription"
              [compact]="true"
            ></app-empty-state>
          } @else {
            <div class="tw-flex tw-flex-col tw-gap-2">
              @for (row of listRows(); track row.listItemId) {
                <ng-container
                  *ngTemplateOutlet="currentListRow; context: { row: row }"
                ></ng-container>
              }
            </div>
          }
        </div>
      } @else {
        <!-- Card · My Grocery List — one card at every breakpoint (list body + divider + totals
             footer), matching the DS 2.1 mobile layout. The collapsible pieces below (search,
             store tabs, list name, list body, divider, footer) each carry mobileListHiddenClass()
             so they hide together on mobile while the card header stays visible. -->
        <div
          class="tw-col-span-3 sm:tw-col-span-1 tw-flex tw-flex-col tw-bg-color-surface-card tw-rounded-2xl tw-p-4 tw-shadow-elevation-card tw-gap-3.5"
        >
          <div class="tw-flex tw-flex-col tw-gap-1">
            <div class="tw-flex tw-items-center tw-gap-5 tw-flex-wrap">
              <h2 class="tw-text-base tw-font-semibold tw-text-color-text-default tw-m-0">
                {{ "groceryBuildList" | i18n }}
              </h2>
              <span class="tw-flex-1"></span>
              <button
                type="button"
                id="grocery-archived-toggle"
                class="tw-flex sm:tw-hidden tw-items-center tw-gap-1.5 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-[13px] tw-font-semibold tw-text-color-text-action"
                (click)="togglePastLists()"
              >
                <icon name="archive" [size]="4" currentColor="color-icon-action"></icon>
                {{ "groceryArchivedLists" | i18n }}
                <icon
                  name="down-arrow"
                  [size]="3"
                  currentColor="color-icon-action"
                  [disableArrowOverride]="true"
                ></icon>
              </button>
              <!-- Mobile-only collapse toggle (no-op/hidden on desktop). Lives outside the
                     collapsible body below (not gated by mobileListExpanded) so it stays
                     clickable and can re-expand the card once collapsed. -->
              <button
                type="button"
                class="tw-hidden sm:tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0.5"
                (click)="mobileListExpanded.set(!mobileListExpanded())"
                [attr.aria-expanded]="mobileListExpanded()"
                [attr.aria-label]="'groceryToggleBuildListAria' | i18n"
              >
                @if (mobileListExpanded()) {
                  <icon
                    name="chevrons-collapse"
                    [size]="5"
                    currentColor="color-icon-default"
                  ></icon>
                } @else {
                  <icon name="chevrons-expand" [size]="5" currentColor="color-icon-default"></icon>
                }
              </button>
            </div>
            <p class="tw-text-xs tw-text-color-text-tertiary tw-m-0 sm:tw-hidden">
              {{ "groceryBuildListHint" | i18n }}
            </p>
          </div>

          <!-- Search — hidden on mobile when the card is collapsed (mobileListHiddenClass,
                 see the always-visible header above for the toggle). Works on web without the
                 extension; only the live "refresh price" action is extension-gated (on web),
                 see refreshRow. Shown on mobile by default too — no separate reveal step. -->
          <div
            class="tw-relative tw-w-full"
            [ngClass]="mobileListHiddenClass()"
            appClickOutside
            appClickOutsideFocusOut
            (clickOutside)="onSearchClickOutside($event)"
            (focusin)="onSearchFocusChange(true)"
            (focusout)="onSearchFocusChange(false)"
            (keydown.arrowdown)="onSearchArrowKey($event)"
            (keydown.arrowup)="onSearchArrowKey($event)"
          >
            <app-search-input
              [sunken]="true"
              [placeholder]="'grocerySearchPlaceholder' | i18n"
              [value]="searchQuery"
              [popup]="{ id: searchResultsId, expanded: hasSearchableQuery() }"
              (valueChange)="onSearchChange($event)"
            ></app-search-input>

            <!-- Floating search dropdown — the list below stays put; it never re-flows.
             A dialog the search input's combobox opens, not a listbox: its rows are buttons.
             Gated on the same 2-char minimum as every branch inside it (the
             "Add custom" row, "no matches", and search itself) — opening it any
             earlier would show an empty bordered card with no content to fill it. -->
            @if (hasSearchableQuery()) {
              <div
                [id]="searchResultsId"
                role="dialog"
                [attr.aria-label]="'grocerySearchResults' | i18n"
                appGlossScroll
                class="tw-absolute tw-top-[calc(100%+8px)] tw-left-0 tw-right-0 tw-z-20 tw-max-h-[420px] tw-overflow-auto tw-bg-color-surface-page tw-border tw-border-color-border-subtle tw-rounded-[14px] tw-shadow-[0px_14px_34px_0px_rgba(20,26,41,0.22)]"
              >
                <!-- Always offer to add the literal typed term, even alongside catalog matches,
                 while a search is loading, or after it failed — adding a custom item
                 doesn't depend on search. -->
                <div
                  [class]="clickableSearchRowClass"
                  appKeyboardActivatable
                  (appKeyboardActivatableActivate)="addCustomFromSearch()"
                  (click)="addCustomFromSearch()"
                >
                  <div class="tw-flex tw-flex-1 tw-flex-col tw-gap-0.5 tw-min-w-0">
                    <p
                      class="tw-text-[13.5px] tw-font-semibold tw-text-color-text-default tw-m-0 tw-truncate"
                    >
                      {{ "groceryAddCustom" | i18n: searchQuery.trim() }}
                    </p>
                    <p class="tw-text-[11.5px] tw-font-medium tw-text-color-text-tertiary tw-m-0">
                      {{ "groceryAddCustomHint" | i18n }}
                    </p>
                  </div>
                  <ng-container
                    *ngTemplateOutlet="
                      addPill;
                      context: {
                        ariaLabel: 'groceryAddCustom' | i18n: searchQuery.trim(),
                        item: null,
                      }
                    "
                  ></ng-container>
                </div>
                <div class="tw-h-px tw-w-full tw-bg-color-border-subtle"></div>

                @if (searchVm(); as vm) {
                  @if (vm.error) {
                    <p class="tw-text-sm tw-text-color-status-error-text tw-py-3 tw-px-4 tw-m-0">
                      {{ "grocerySearchError" | i18n }}
                    </p>
                  } @else if (vm.rows.length > 0) {
                    <div class="tw-flex tw-items-start tw-pl-4 tw-pt-2.5 tw-pb-0.5">
                      <p
                        class="tw-text-[10.5px] tw-font-semibold tw-tracking-[0.84px] tw-text-color-text-tertiary tw-uppercase tw-m-0"
                      >
                        {{ "groceryPricedMatches" | i18n: vm.total }}
                      </p>
                    </div>
                    @for (row of visibleSearchRows(); track row.item.id; let last = $last) {
                      <ng-container
                        *ngTemplateOutlet="searchRow; context: { row: row, isLast: last }"
                      ></ng-container>
                    }
                    @if (searchDropdownExpanded() && searchLoadMoreFailed()) {
                      <!-- The auto-pager gave up after a failed page — offer a way back in. -->
                      <div class="tw-h-px tw-w-full tw-bg-color-border-subtle"></div>
                      <button
                        type="button"
                        class="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-h-11 tw-w-full tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-[12.5px] tw-font-semibold tw-text-color-text-action"
                        (click)="expandSearchDropdown()"
                      >
                        {{ "groceryShowAllRetry" | i18n }}
                      </button>
                    } @else if (
                      !searchDropdownExpanded() && visibleSearchRows().length < vm.total
                    ) {
                      <div class="tw-h-px tw-w-full tw-bg-color-border-subtle"></div>
                      <button
                        type="button"
                        class="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-h-11 tw-w-full tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-[12.5px] tw-font-semibold tw-text-color-text-action"
                        (click)="expandSearchDropdown()"
                      >
                        {{ "groceryShowAllMatches" | i18n: vm.total }}
                        <icon
                          name="chevron-down"
                          [size]="4"
                          currentColor="color-icon-action"
                        ></icon>
                      </button>
                    } @else if (searchDropdownExpanded() && vm.hasMore) {
                      <!-- Still auto-paging, or MAX_SHOW_ALL_ROWS was hit with more left on the server. -->
                      <div class="tw-h-px tw-w-full tw-bg-color-border-subtle"></div>
                      <p
                        class="tw-text-[11.5px] tw-text-color-text-tertiary tw-text-center tw-py-3 tw-px-4 tw-m-0"
                      >
                        @if (searchDropdownAtCap()) {
                          {{ "groceryShowAllCapped" | i18n: visibleSearchRows().length }}
                        } @else {
                          {{ "groceryLoadingMore" | i18n }}
                        }
                      </p>
                    }
                  } @else if (vm.loading) {
                    <p class="tw-text-sm tw-text-color-text-tertiary tw-py-3 tw-px-4 tw-m-0">
                      {{ "groceryLoading" | i18n }}
                    </p>
                  } @else {
                    <p class="tw-text-sm tw-text-color-text-tertiary tw-py-3 tw-px-4 tw-m-0">
                      {{ "groceryNoMatches" | i18n }}
                    </p>
                  }
                }
              </div>
            }
          </div>

          <!-- Store tabs — hidden on mobile when the card is collapsed, same as Search above. -->
          <div
            class="tw-flex tw-w-full tw-items-center tw-rounded-2xl tw-bg-color-surface-sunken tw-p-1 tw-gap-1"
            [ngClass]="mobileListHiddenClass()"
          >
            @for (tab of storeTabs; track tab.key) {
              <app-tab-button
                class="tw-flex-1"
                [label]="tab.label"
                [isActive]="selectedStore() === tab.key"
                (tabClick)="selectStoreTab(tab.key)"
              ></app-tab-button>
            }
          </div>

          <!-- Active list name + rename — the only entry point to renameList(), so it stays
                   visible on mobile too (hidden only while the card is collapsed). -->
          <div
            class="tw-flex tw-items-center tw-justify-between tw-gap-2"
            [ngClass]="mobileListHiddenClass()"
          >
            <p
              class="tw-text-xs tw-text-color-text-tertiary tw-font-medium tw-m-0 tw-min-w-0 tw-truncate"
            >
              {{ activeList()?.name || ("groceryYourList" | i18n) }}
            </p>
            @if (activeList()) {
              <button
                type="button"
                class="tw-text-xs tw-text-color-text-secondary tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-shrink-0 hover:tw-text-color-text-default hover:tw-underline"
                (click)="renameList()"
              >
                {{ "groceryRenameList" | i18n }}
              </button>
            }
          </div>

          <!-- Your list — filtered to the active store tab; "Best" hides unpriced items.
               Hidden on mobile when the card is collapsed, same as Search/Store tabs above. -->
          <div [ngClass]="mobileListHiddenClass()">
            @if (listStatus() === "loading") {
              <p class="tw-text-sm tw-text-color-text-tertiary tw-py-2 tw-m-0">
                {{ "groceryListLoading" | i18n }}
              </p>
            } @else if (listStatus() === "error") {
              <p class="tw-text-sm tw-text-color-text-tertiary tw-py-2 tw-m-0">
                {{ "groceryListError" | i18n }}
              </p>
            } @else if (listRows().length === 0) {
              <app-empty-state
                titleKey="groceryEmptyStateTitle"
                descriptionKey="groceryEmptyStateDescription"
                [compact]="true"
              ></app-empty-state>
            } @else if (visibleListRows().length === 0) {
              <p class="tw-text-sm tw-text-color-text-tertiary tw-py-2 tw-m-0">
                {{
                  selectedStore() === "best"
                    ? ("groceryNoPricedItems" | i18n)
                    : ("groceryNoItemsAtStore" | i18n: selectedStoreTabLabel())
                }}
              </p>
            } @else if (selectedStore() === "best") {
              <!-- "Best": grouped by the store each item is cheapest at, one chip per item. -->
              <div class="tw-flex tw-flex-col tw-gap-5">
                @for (group of bestStoreGroups(); track group.key) {
                  <section class="tw-flex tw-flex-col tw-gap-3">
                    <header class="tw-flex tw-items-center tw-justify-between tw-gap-2">
                      <h3
                        class="tw-m-0 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-color-text-tertiary"
                      >
                        {{
                          (group.rows.length === 1
                            ? "groceryGroupHeaderOne"
                            : "groceryGroupHeaderMany"
                          ) | i18n: group.label : group.rows.length
                        }}
                      </h3>
                      @if (group.subtotal !== null) {
                        <span class="tw-text-[13px] tw-font-semibold tw-text-color-text-default">
                          {{ formatPrice(group.subtotal) }}
                        </span>
                      }
                    </header>
                    @for (row of group.rows; track row.listItemId) {
                      <ng-container
                        *ngTemplateOutlet="
                          priceRow;
                          context: { row: row, removable: true, flat: true }
                        "
                      ></ng-container>
                    }
                  </section>
                }
              </div>
            } @else {
              <div class="tw-flex tw-flex-col tw-gap-3">
                @for (row of visibleListRows(); track row.listItemId) {
                  <ng-container
                    *ngTemplateOutlet="priceRow; context: { row: row, removable: true }"
                  ></ng-container>
                }
              </div>
            }
          </div>

          @if (
            (listStatus() === "loaded" && listRows().length > 0) ||
            (activeList()?.items?.length ?? 0) > 0
          ) {
            <div
              class="tw-h-px tw-w-full tw-bg-color-border-subtle"
              [ngClass]="mobileListHiddenClass()"
            ></div>

            <!-- Footer · totals & archive. Collapses along with the body above on mobile (see
                 mobileListExpanded) — a collapsed card shows only its header. -->
            <div
              class="tw-flex tw-items-center tw-gap-3 tw-w-full"
              [ngClass]="mobileListHiddenClass()"
            >
              @if (listStatus() === "loaded" && listRows().length > 0) {
                <div class="tw-flex tw-flex-col tw-gap-0.5">
                  <p
                    class="tw-text-sm sm:tw-text-xs tw-font-semibold tw-text-color-text-secondary tw-uppercase tw-m-0 sm:tw-font-montserrat sm:tw-leading-none sm:tw-tracking-normal"
                  >
                    {{ "groceryTotalLabel" | i18n: selectedStoreTabLabel() }}
                  </p>
                  <p
                    class="tw-text-2xl tw-font-semibold tw-text-color-text-default tw-m-0 sm:tw-font-montserrat sm:tw-leading-none sm:tw-tracking-normal"
                  >
                    <span class="sm:tw-hidden">$</span>{{ footerTotal() | number: "1.2-2"
                    }}<span class="tw-hidden sm:tw-inline"> {{ "groceryCurrencyAud" | i18n }}</span>
                  </p>
                </div>
              }
              <!-- Default store indicator — mobile only; desktop shows this in the Price
                   Comparison card's per-store row instead. Only on the default store's own tab:
                   the total beside it is that store's, so it'd mislead under another tab or "Best". -->
              @if (defaultStore() === selectedStore()) {
                <div class="tw-hidden sm:tw-flex tw-items-center tw-gap-1.5 sm:tw-ml-auto">
                  <span
                    class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-full tw-bg-color-accent-today-bg tw-text-color-text-action tw-text-[13px] tw-font-medium tw-font-montserrat tw-leading-none tw-tracking-normal tw-whitespace-nowrap"
                  >
                    {{ "groceryDefault" | i18n }}
                  </span>
                  <icon
                    name="lock"
                    [size]="4"
                    currentColor="color-icon-secondary"
                    aria-hidden="true"
                  ></icon>
                </div>
              }
              <span class="tw-flex-1 sm:tw-hidden"></span>
              @if ((activeList()?.items?.length ?? 0) > 0) {
                <ng-container
                  *ngTemplateOutlet="
                    archiveListButton;
                    context: { layoutClass: 'sm:tw-hidden tw-flex', hiddenClass: '' }
                  "
                ></ng-container>
              }
            </div>
            @if (
              listStatus() === "loaded" &&
              (footerNotCarriedCount() > 0 || footerAwaitingPriceCount() > 0)
            ) {
              <p
                class="tw-hidden sm:tw-block tw-text-[11.5px] tw-font-medium tw-text-color-text-tertiary tw-m-0"
                [ngClass]="mobileListHiddenClass()"
              >
                @if (footerNotCarriedCount() > 0 && footerAwaitingPriceCount() > 0) {
                  {{
                    "groceryExcludesBoth"
                      | i18n: footerNotCarriedCount() : footerAwaitingPriceCount()
                  }}
                } @else if (footerNotCarriedCount() > 0) {
                  {{ "groceryExcludesNotCarried" | i18n: footerNotCarriedCount() }}
                } @else {
                  {{ "groceryExcludesAwaitingPrice" | i18n: footerAwaitingPriceCount() }}
                }
              </p>
            }
            <!-- Mobile-only full-width archive button, below the totals row (see the DS 2.1
                 mobile layout) — desktop keeps it inline with the total (above). -->
            @if ((activeList()?.items?.length ?? 0) > 0) {
              <ng-container
                *ngTemplateOutlet="
                  archiveListButton;
                  context: {
                    layoutClass: 'tw-hidden sm:tw-flex sm:tw-w-full',
                    hiddenClass: mobileListHiddenClass(),
                  }
                "
              ></ng-container>
            }
            <ng-template
              #archiveListButton
              let-layoutClass="layoutClass"
              let-hiddenClass="hiddenClass"
            >
              <button
                type="button"
                class="tw-items-center tw-justify-center tw-h-10 tw-px-6 tw-rounded-2xl tw-bg-color-action-primary tw-text-color-text-on-action tw-text-sm tw-font-semibold tw-border-none tw-cursor-pointer tw-shadow-elevation-card hover:tw-bg-color-action-primary-hover disabled:tw-opacity-50 disabled:tw-cursor-default"
                [ngClass]="[layoutClass, hiddenClass]"
                [disabled]="archiving() || archivingItems().size > 0"
                (click)="archiveList()"
              >
                {{ "groceryArchiveList" | i18n }}
              </button>
            </ng-template>
          }
        </div>
      }

      <!-- Card · Price comparison — main list view only; the Archived Lists view shows Current list in this slot. -->
      @if (!showPastLists()) {
        <div
          class="tw-col-span-2 sm:tw-col-span-1 tw-flex tw-flex-col tw-bg-color-surface-card tw-rounded-2xl tw-p-4 tw-shadow-elevation-card tw-gap-3.5"
        >
          @let comparisonSummary = summary();
          <div class="tw-flex tw-flex-col tw-gap-1">
            <div class="tw-flex tw-items-center tw-gap-5 tw-flex-wrap">
              <h2 class="tw-text-base tw-font-semibold tw-text-color-text-default tw-m-0">
                {{ "groceryPriceComparisonTitle" | i18n }}
              </h2>
              <span class="tw-flex-1"></span>
              <!-- Mobile-only collapse toggle (hidden on desktop). Starts expanded and collapses on
                 tap (see mobilePriceComparisonExpanded). Only shown once there is a body to
                 collapse — not while loading, on error, or before any priced item exists. -->
              @if (listStatus() === "loaded" && comparisonSummary) {
                <button
                  type="button"
                  class="tw-hidden sm:tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0.5"
                  (click)="mobilePriceComparisonExpanded.set(!mobilePriceComparisonExpanded())"
                  [attr.aria-expanded]="mobilePriceComparisonExpanded()"
                  [attr.aria-label]="'groceryTogglePriceComparisonAria' | i18n"
                >
                  @if (mobilePriceComparisonExpanded()) {
                    <icon
                      name="chevrons-collapse"
                      [size]="5"
                      currentColor="color-icon-default"
                    ></icon>
                  } @else {
                    <icon
                      name="chevrons-expand"
                      [size]="5"
                      currentColor="color-icon-default"
                    ></icon>
                  }
                </button>
              }
            </div>
            @if (listStatus() === "loading") {
              <p class="tw-text-xs tw-text-color-text-tertiary tw-m-0">
                {{ "groceryListLoading" | i18n }}
              </p>
            } @else if (listStatus() === "error") {
              <p class="tw-text-xs tw-text-color-text-tertiary tw-m-0">
                {{ "groceryListError" | i18n }}
              </p>
            } @else if (comparisonSummary) {
              <p class="tw-text-xs tw-text-color-text-tertiary tw-m-0">
                {{ "groceryPriceComparisonSubtitle" | i18n: comparisonSummary.itemCount }}
              </p>
            } @else {
              <p class="tw-text-xs tw-text-color-text-tertiary tw-m-0">
                {{ "groceryPriceComparisonEmptyHint" | i18n: comparisonStoreNames }}
              </p>
            }
          </div>

          @if (listStatus() === "loaded" && comparisonSummary) {
            <div
              class="tw-flex tw-flex-col tw-gap-2"
              [ngClass]="mobilePriceComparisonHiddenClass()"
            >
              @for (store of comparisonSummary.storeTotals; track store.store) {
                @let hasMissing = store.missing.length > 0;
                @let isCheapest = comparisonSummary.bestStore === store.store;
                <div
                  class="tw-relative tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-xl tw-px-4 tw-py-3.5"
                  [ngClass]="
                    isCheapest
                      ? 'tw-bg-color-status-success-bg tw-border tw-border-solid tw-border-color-status-success-border'
                      : 'tw-bg-color-surface-sunken'
                  "
                >
                  <div class="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0 tw-flex-1 tw-basis-[120px]">
                    <p
                      class="tw-text-sm tw-font-medium tw-m-0 tw-truncate"
                      [ngClass]="
                        isCheapest
                          ? 'tw-text-color-status-success-text'
                          : 'tw-text-color-text-default'
                      "
                    >
                      {{ store.label }}
                    </p>
                    <span
                      appClickOutside
                      appClickOutsideFocusOut
                      (clickOutside)="onMissingClickOutside($event, store.store, missingButton)"
                    >
                      <button
                        #missingButton
                        type="button"
                        class="tw-text-xs tw-m-0 tw-p-0 tw-bg-transparent tw-border-none tw-text-left tw-break-words"
                        [ngClass]="[
                          hasMissing
                            ? 'tw-cursor-pointer tw-underline-offset-2 hover:tw-underline'
                            : 'tw-cursor-default',
                          isCheapest
                            ? 'tw-text-color-status-success-text'
                            : 'tw-text-color-text-tertiary',
                        ]"
                        [disabled]="!hasMissing"
                        (click)="toggleMissing(store.store, missingButton)"
                        [attr.aria-label]="
                          hasMissing ? ('groceryMissingAria' | i18n: store.label) : null
                        "
                        [attr.aria-haspopup]="hasMissing ? 'dialog' : null"
                        [attr.aria-expanded]="hasMissing ? openPopover() === store.store : null"
                        [attr.aria-controls]="
                          hasMissing ? 'grocery-missing-panel-' + store.store : null
                        "
                      >
                        {{
                          hasMissing
                            ? ("groceryStoreItemsNotCarried" | i18n: store.missing.length)
                            : ("groceryStoreHasAllItems" | i18n: comparisonSummary.itemCount)
                        }}
                      </button>
                      @if (hasMissing && openPopover() === store.store) {
                        <div
                          [id]="'grocery-missing-panel-' + store.store"
                          role="dialog"
                          [attr.aria-label]="'groceryNotCarriedAt' | i18n: store.label"
                          tabindex="-1"
                          class="tw-absolute tw-top-[calc(100%+6px)] tw-left-0 tw-z-10 tw-min-w-[200px] tw-bg-color-surface-card tw-border tw-border-color-border-subtle tw-rounded-lg tw-p-3 tw-shadow-elevation-overlay"
                        >
                          <p
                            class="tw-text-xs tw-font-medium tw-text-color-text-secondary tw-m-0 tw-mb-1"
                          >
                            {{ "groceryNotCarriedAt" | i18n: store.label }}
                          </p>
                          <ul class="tw-m-0 tw-pl-4">
                            @for (name of store.missing; track name) {
                              <li class="tw-text-sm tw-text-color-text-default">{{ name }}</li>
                            }
                          </ul>
                        </div>
                      }
                    </span>
                  </div>

                  <div
                    class="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-justify-end tw-ml-auto"
                  >
                    @if (isCheapest) {
                      <span
                        class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-full tw-bg-color-status-success tw-text-color-text-on-action tw-text-[13px] tw-font-medium tw-whitespace-nowrap"
                      >
                        <!-- Only a store carrying the whole list is honestly "Cheapest": the highlight
                       ranks coverage first, so an incomplete winner can show a higher total than
                       a store listed below it (and sits next to "no store carries everything"). -->
                        {{ (hasMissing ? "groceryBestPick" : "groceryCheapest") | i18n }}
                      </span>
                    }
                    @if (defaultStore() === store.store) {
                      <span
                        class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-full tw-bg-color-accent-today-bg tw-text-color-text-action tw-text-[13px] tw-font-medium tw-whitespace-nowrap"
                      >
                        {{ "groceryDefault" | i18n }}
                      </span>
                    }

                    <p
                      class="tw-text-base tw-font-semibold tw-m-0 tw-whitespace-nowrap"
                      [ngClass]="
                        isCheapest
                          ? 'tw-text-color-status-success-text'
                          : 'tw-text-color-text-default'
                      "
                    >
                      ${{ store.total | number: "1.2-2" }}
                    </p>
                  </div>
                </div>
              }
            </div>

            @if (
              comparisonSummary.bestCompleteStore &&
              comparisonSummary.savingsFromSplit !== null &&
              comparisonSummary.savingsFromSplit > 0
            ) {
              <p
                class="tw-text-sm tw-text-color-text-secondary tw-m-0"
                [ngClass]="mobilePriceComparisonHiddenClass()"
              >
                {{
                  "grocerySavingsNote"
                    | i18n
                      : (comparisonSummary.savingsFromSplit | number: "1.2-2")
                      : comparisonSummary.bestCompleteLabel
                }}
              </p>
            } @else if (!comparisonSummary.bestCompleteStore) {
              <p
                class="tw-text-sm tw-text-color-text-secondary tw-m-0"
                [ngClass]="mobilePriceComparisonHiddenClass()"
              >
                {{ "groceryNoCompleteStore" | i18n }}
              </p>
            }

            <!-- Freshness · all breakpoints (collapses with the card on mobile) -->
            <div
              class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-pt-0.5"
              [ngClass]="mobilePriceComparisonHiddenClass()"
            >
              <span class="tw-text-[11.5px] tw-text-color-text-tertiary">
                @if (latestPriceLabel(); as latestLabel) {
                  {{ "groceryPricesUpdated" | i18n: latestLabel }}
                } @else {
                  {{ "groceryPricesNeverUpdated" | i18n }}
                }
              </span>
              <button
                type="button"
                class="tw-flex tw-items-center tw-gap-1.5 tw-bg-transparent tw-border-none tw-rounded-lg tw-px-1.5 tw-py-1 tw-cursor-pointer tw-text-color-text-action tw-text-[12.5px] tw-font-semibold tw-whitespace-nowrap disabled:tw-opacity-50 disabled:tw-cursor-default hover:tw-bg-color-surface-elevated"
                [disabled]="!hasRefreshableRows() || refreshingAll()"
                (click)="refreshAllPrices()"
              >
                <icon name="refresh-line" [size]="4" currentColor="color-icon-action"></icon>
                {{
                  (refreshingAll() ? "groceryRefreshingPrices" : "groceryRefreshAllPrices") | i18n
                }}
              </button>
            </div>

            <div
              class="tw-h-px tw-w-full tw-bg-color-border-subtle"
              [ngClass]="mobilePriceComparisonHiddenClass()"
            ></div>

            <!-- Split across stores -->
            <div
              class="tw-flex tw-flex-col tw-gap-1 tw-bg-color-surface-sunken tw-rounded-xl tw-p-4"
              [ngClass]="mobilePriceComparisonHiddenClass()"
            >
              <p
                class="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-color-text-tertiary tw-m-0"
              >
                {{ "grocerySplitLabel" | i18n }}
              </p>
              <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                <p class="tw-text-[28px] tw-font-semibold tw-text-color-text-default tw-m-0">
                  ${{ comparisonSummary.splitTotal | number: "1.2-2" }}
                </p>
                @if (splitStoresLabel()) {
                  <p class="tw-text-[13px] tw-text-color-text-secondary tw-m-0 tw-text-right">
                    {{ splitStoresLabel() }}
                  </p>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>
  } @else {
    <div
      class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-text-center tw-bg-color-surface-card tw-rounded-2xl tw-p-8 xs:tw-p-6 tw-shadow-elevation-card tw-w-full tw-max-w-xl tw-mx-auto"
      data-testid="grocery-plugin-required"
    >
      <icon name="grocery" [size]="8" currentColor="color-icon-secondary"></icon>
      <h2 class="tw-text-lg tw-font-semibold tw-text-color-text-default tw-m-0">
        {{ "groceryPluginRequiredTitle" | i18n }}
      </h2>
      <p class="tw-text-sm tw-text-color-text-secondary tw-m-0">
        {{ "groceryPluginRequiredBody" | i18n }}
      </p>
      <button
        type="button"
        class="tw-flex tw-items-center tw-justify-center tw-h-10 tw-px-6 tw-rounded-2xl tw-bg-color-action-primary tw-text-color-text-on-action tw-text-sm tw-font-semibold tw-border-none tw-cursor-pointer tw-shadow-elevation-card hover:tw-bg-color-action-primary-hover"
        (click)="goToGroceryPlugin()"
      >
        {{ "groceryPluginRequiredCta" | i18n }}
      </button>
    </div>
  }
</app-page-wrapper>

<!-- Shared store-price chips, used by search results and the list's grouped "Best" rows. -->
<ng-template #chipList let-chips="chips">
  @for (chip of chips; track chip.store) {
    <span
      class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-rounded-full tw-font-medium tw-whitespace-nowrap"
      [ngClass]="[
        chip.isHighlighted
          ? 'tw-bg-color-status-success-bg tw-text-color-status-success-text'
          : 'tw-bg-color-surface-sunken tw-text-color-text-secondary',
        chips.length > 1
          ? 'tw-min-w-0 tw-px-3 tw-text-[13px] xs:tw-px-2 xs:tw-text-[11px]'
          : 'tw-min-w-[60px] tw-px-3 tw-text-[13px]',
      ]"
    >
      {{ chip.label }} {{ formatPrice(chip.price) }}
    </span>
  }
</ng-template>

<!-- Shared per-item "refresh prices" result message, used by both search results and the list. -->
<ng-template #refreshMessage let-row="row">
  @if (refreshState()[row.item.id]; as state) {
    @if (state.message) {
      <!-- A search-dropdown row is click-to-add (see searchRow); stop the click here so
           tapping the status text doesn't also add the product. -->
      <p
        class="tw-text-[11px] tw-m-0"
        [class.tw-text-color-status-error-text]="state.error"
        [class.tw-text-color-text-tertiary]="!state.error"
        (click)="$event.stopPropagation()"
      >
        {{ state.message }}
      </p>
    }
    <!-- Live refresh needs the Gloss web extension and it's absent: offer an inline
         install link instead of redirecting to /plugins from inside the gate (#5942).
         Nested in a keyboard-activatable row, so stop propagation on click + Enter/Space
         (see clickableSearchRowClass) so activating it doesn't also fire the row.
         self-start + text-left keep it label-width and left-aligned in the flex column. -->
    @if (state.extensionMissing) {
      <button
        type="button"
        class="tw-self-start tw-text-left tw-bg-transparent tw-border-none tw-p-0 tw-mt-0.5 tw-cursor-pointer tw-text-color-text-action tw-text-[11px] tw-font-semibold tw-underline"
        (click)="$event.stopPropagation(); goToInstallExtension()"
        (keydown.enter)="$event.stopPropagation()"
        (keydown.space)="$event.stopPropagation()"
      >
        {{ "groceryInstallExtensionCta" | i18n }}
      </button>
    }
  }
</ng-template>

<!-- Shared "Add" pill, used by both the custom-add row (item=null) and a catalog search result. -->
<ng-template #addPill let-ariaLabel="ariaLabel" let-item="item">
  <button
    type="button"
    class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-lg tw-bg-color-surface-card tw-text-color-text-action tw-text-[13px] tw-font-medium tw-font-montserrat tw-leading-none tw-tracking-normal tw-shadow-elevation-card tw-border-none tw-cursor-pointer tw-shrink-0"
    [attr.aria-label]="ariaLabel"
    (click)="$event.stopPropagation(); item ? addItem(item) : addCustomFromSearch()"
    (keydown.enter)="$event.stopPropagation()"
    (keydown.space)="$event.stopPropagation()"
  >
    {{ "groceryAdd" | i18n }}
  </button>
</ng-template>

<!-- "Request price" button (or its "requested" note) for a custom list item. -->
<ng-template #requestPriceAction let-row="row" let-compact="compact">
  @if (priceRequested().has(row.item.id)) {
    <span
      class="tw-text-color-text-tertiary tw-italic"
      [ngClass]="compact ? 'tw-text-[10.5px] tw-tracking-tight' : 'tw-text-xs tw-ml-1'"
    >
      {{ "groceryPriceRequested" | i18n }}
    </span>
  } @else {
    <button
      type="button"
      class="tw-border-none tw-bg-transparent tw-text-color-text-action tw-cursor-pointer tw-font-semibold"
      [ngClass]="
        compact
          ? 'tw-px-0 tw-text-[10.5px] tw-leading-none tw-tracking-tight'
          : 'tw-px-1 tw-text-xs !tw-leading-none'
      "
      (click)="$event.stopPropagation(); requestPrice(row)"
      (keydown.enter)="$event.stopPropagation()"
      (keydown.space)="$event.stopPropagation()"
      [attr.title]="'groceryRequestPrice' | i18n"
    >
      {{ "groceryRequestPrice" | i18n }}
    </button>
  }
</ng-template>

<!-- Shared price-chip + unit-price row, used by both search results and the list. -->
<ng-template #priceRow let-row="row" let-removable="removable" let-flat="flat">
  @let phone = isSmallScreen();
  <div
    class="tw-flex tw-items-center tw-gap-3 tw-bg-color-surface-card tw-rounded-2xl tw-px-4 tw-py-3 tw-shadow-elevation-card"
    [class.tw-cursor-pointer]="removable"
    (mousedown)="onRowMouseDown()"
    (click)="onRowClick($event, row, removable)"
  >
    <!-- Tick off (list rows only): "I bought this". -->
    @if (removable) {
      <span
        class="tw-relative tw-inline-flex tw-items-center tw-justify-center tw-shrink-0 tw-size-[22px]"
      >
        <input
          type="checkbox"
          class="tw-appearance-none tw-size-[22px] tw-rounded-full tw-border-[1.5px] tw-cursor-pointer tw-m-0"
          [ngClass]="
            row.purchased
              ? 'tw-bg-color-action-primary tw-border-color-action-primary'
              : 'tw-bg-color-surface-input tw-border-color-border-subtle'
          "
          [checked]="row.purchased"
          (click)="$event.stopPropagation(); togglePurchased(row)"
          [attr.aria-label]="'groceryTickOff' | i18n"
        />
        @if (row.purchased) {
          <icon
            name="check-only"
            [size]="3"
            currentColor="color-text-on-action"
            class="tw-absolute tw-pointer-events-none"
          ></icon>
        }
      </span>
    }

    <div class="tw-flex tw-flex-1 tw-flex-col tw-gap-1 tw-min-w-0">
      <!-- First row: the name alone, so a product carried by several stores never truncates it. -->
      <div class="tw-flex tw-items-center tw-gap-2 tw-w-full tw-min-w-0">
        <span
          class="tw-text-sm tw-min-w-0 tw-truncate"
          [ngClass]="
            row.purchased
              ? 'tw-line-through tw-text-color-text-tertiary'
              : 'tw-text-color-text-default'
          "
        >
          {{ row.item.name }}
        </span>
        @if (!row.isCustom && row.item.category && !phone) {
          <span class="tw-text-[12px] tw-text-color-text-tertiary tw-whitespace-nowrap">
            {{ row.item.category }}
          </span>
        }
        <!-- Small screen, store tab: the store's chip is a plain price pill, so flag the
             cheapest store here instead. -->
        @if (phone && removable && selectedStore() !== "best" && selectedChip(row)?.isHighlighted) {
          <span
            class="tw-flex tw-items-center tw-h-6 tw-px-2.5 tw-shrink-0 tw-rounded-full tw-bg-color-status-success-bg tw-text-color-status-success-text tw-text-xs tw-font-medium tw-whitespace-nowrap"
          >
            {{ "groceryCheapest" | i18n }}
          </span>
        }
      </div>

      <!-- Second row: custom items carry their status + request link, priced items the unit price
           on the left and the store chip on the right. -->
      @if (row.isCustom) {
        @if (phone) {
          <!-- Phone: "Just added · No price yet · Request price" on one never-wrapping line; the
               note truncates if it must. -->
          <div class="tw-flex tw-items-center tw-gap-1.5 tw-w-full">
            <p
              class="tw-min-w-0 tw-truncate tw-tracking-tight tw-text-[10.5px] tw-text-color-text-tertiary tw-m-0"
            >
              {{ "groceryJustAddedNote" | i18n }}
            </p>
            <span
              class="tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-h-6 tw-px-1.5 tw-rounded-full tw-bg-color-status-warning-bg tw-text-color-status-warning-text tw-text-[10.5px] tw-font-medium tw-whitespace-nowrap"
            >
              {{ "groceryNoPriceYet" | i18n }}
            </span>
            <span class="tw-ml-auto tw-shrink-0">
              <ng-container
                *ngTemplateOutlet="requestPriceAction; context: { row: row, compact: true }"
              ></ng-container>
            </span>
          </div>
        } @else {
          <div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-w-full">
            <p class="tw-text-[12px] tw-text-color-text-tertiary tw-m-0">
              {{ "groceryAddedByYouNote" | i18n }}
            </p>
            <span class="tw-flex tw-items-center tw-gap-1.5 tw-shrink-0">
              <span
                class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-full tw-bg-color-status-warning-bg tw-text-color-status-warning-text tw-text-[13px] tw-font-medium tw-whitespace-nowrap"
              >
                {{ "groceryNoPriceYet" | i18n }}
              </span>
              <ng-container
                *ngTemplateOutlet="requestPriceAction; context: { row: row }"
              ></ng-container>
            </span>
          </div>
        }
      } @else {
        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1.5 tw-w-full">
          <p
            class="tw-text-[12px] tw-m-0 tw-whitespace-nowrap"
            [ngClass]="
              row.isBestValue ? 'tw-text-color-status-success-text' : 'tw-text-color-text-tertiary'
            "
          >
            {{ formatPrice(row.unitPrice.value) }}{{ row.unitPrice.label }}
            <!-- Dropped on phones and in grouped rows: the green unit price already says it, and
                 the pill would squeeze the price chip. -->
            @if (row.isBestValue && !flat && !phone) {
              <span
                class="tw-text-[11px] tw-font-medium tw-text-color-status-success-text tw-bg-color-status-success-bg tw-px-2 tw-py-0.5 tw-rounded tw-ml-2"
              >
                {{ "groceryBestValue" | i18n }}
              </span>
            }
          </p>
          <span class="tw-flex tw-items-center tw-gap-1.5 tw-ml-auto tw-shrink-0">
            @if (selectedStore() !== "best") {
              <!-- List row, specific store tab: single chip for that store. -->
              @if (selectedChip(row); as chip) {
                <span
                  class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-full tw-text-[13px] tw-font-medium tw-whitespace-nowrap sm:tw-bg-color-surface-sunken sm:tw-text-color-text-secondary"
                  [ngClass]="
                    chip.isHighlighted
                      ? 'tw-bg-color-status-success-bg tw-text-color-status-success-text'
                      : 'tw-bg-color-surface-sunken tw-text-color-text-secondary'
                  "
                >
                  {{ formatPrice(chip.price) }}
                </span>
              } @else {
                <!-- Row visible on this tab despite no chip for it — see visibleListRows. Either
                     this store doesn't carry the item (has other prices), or nobody has priced it
                     at all (no prices anywhere). Those are different claims, so word them differently. -->
                <span
                  class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-full tw-bg-color-surface-sunken tw-text-color-text-tertiary tw-text-[13px] tw-font-medium tw-whitespace-nowrap"
                >
                  {{ (row.chips.length ? "groceryNotCarriedChip" : "groceryNoPriceYet") | i18n }}
                </span>
              }
            } @else {
              <!-- "Best" group row: just the cheapest store's chip (the group header names the store). -->
              @if (cheapestChip(row); as chip) {
                <ng-container
                  *ngTemplateOutlet="chipList; context: { chips: [chip] }"
                ></ng-container>
              }
            }
          </span>
        </div>
      }
      <ng-container *ngTemplateOutlet="refreshMessage; context: { row: row }"></ng-container>
    </div>
    <!-- Row menu sits beside the whole content column, so it is vertically centred across both rows. -->
    @if (removable && !isSmallScreen()) {
      <span
        class="tw-relative"
        appClickOutside
        (clickOutside)="onRowMenuClickOutside($event, row.listItemId, rowMenuTrigger)"
      >
        <button
          #rowMenuTrigger
          type="button"
          (click)="$event.stopPropagation(); toggleRowMenu(row.listItemId, rowMenuTrigger)"
          class="tw-flex tw-items-center tw-justify-center tw-p-1.5 tw-rounded-lg tw-bg-transparent tw-border-none tw-cursor-pointer hover:tw-bg-color-surface-elevated"
          [attr.aria-label]="'groceryRowMenuAria' | i18n"
          aria-haspopup="menu"
          [attr.aria-expanded]="openRowMenu() === row.listItemId"
        >
          <icon name="more-horizontal" [size]="4" currentColor="color-icon-secondary"></icon>
        </button>
        @if (openRowMenu() === row.listItemId) {
          <div
            role="menu"
            (keydown)="onRowMenuKeydown($event, rowMenuTrigger)"
            class="tw-absolute tw-top-[calc(100%+4px)] tw-right-0 tw-z-30 tw-w-[268px] tw-bg-color-surface-card tw-border tw-border-solid tw-border-color-border-subtle tw-rounded-xl tw-py-1.5 tw-shadow-[0px_12px_28px_0px_rgba(20,26,41,0.22)]"
          >
            @if (!row.isCustom) {
              <button
                type="button"
                role="menuitem"
                class="tw-flex tw-items-center tw-gap-2.5 tw-w-full tw-px-3.5 tw-py-2 tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-left disabled:tw-opacity-50 disabled:tw-cursor-default hover:tw-bg-color-surface-elevated"
                [disabled]="refreshState()[row.item.id]?.loading"
                (click)="
                  $event.stopPropagation();
                  refreshRow(row, removable);
                  closeRowMenu(true, rowMenuTrigger)
                "
              >
                <icon
                  name="refresh-line"
                  [size]="4"
                  currentColor="color-icon-secondary"
                  class="tw-shrink-0"
                ></icon>
                <span class="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0">
                  <span class="tw-text-[13px] tw-font-semibold tw-text-color-text-default">
                    {{
                      (refreshState()[row.item.id]?.loading
                        ? "groceryRefreshingPrices"
                        : "groceryRowMenuRefreshPrice"
                      ) | i18n
                    }}
                  </span>
                  <span class="tw-text-[11px] tw-font-medium tw-text-color-text-tertiary">
                    {{ "groceryRefreshPriceHint" | i18n }}
                  </span>
                </span>
              </button>
            }
            <button
              type="button"
              role="menuitem"
              class="tw-flex tw-items-center tw-gap-2.5 tw-w-full tw-px-3.5 tw-py-2 tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-left disabled:tw-opacity-50 disabled:tw-cursor-default hover:tw-bg-color-surface-elevated"
              [disabled]="archivingItems().has(row.listItemId) || archiving()"
              (click)="archiveItem(row); closeRowMenu(true, rowMenuTrigger)"
            >
              <icon
                name="archive"
                [size]="4"
                currentColor="color-icon-secondary"
                class="tw-shrink-0"
              ></icon>
              <span class="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0">
                <span class="tw-text-[13px] tw-font-semibold tw-text-color-text-default">
                  {{ "groceryArchiveItem" | i18n }}
                </span>
                <span class="tw-text-[11px] tw-font-medium tw-text-color-text-tertiary">
                  {{ "groceryArchiveItemHint" | i18n }}
                </span>
              </span>
            </button>
            <div role="separator" class="tw-h-px tw-w-full tw-bg-color-border-subtle"></div>
            <button
              type="button"
              role="menuitem"
              class="tw-flex tw-items-center tw-gap-2.5 tw-w-full tw-h-11 tw-px-3.5 tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-left disabled:tw-opacity-50 disabled:tw-cursor-default hover:tw-bg-color-surface-elevated"
              [disabled]="archivingItems().has(row.listItemId)"
              (click)="removeItem(row.listItemId); closeRowMenu(true, rowMenuTrigger)"
            >
              <icon
                name="trash-bin"
                [size]="4"
                currentColor="color-status-error-text"
                class="tw-shrink-0"
              ></icon>
              <span class="tw-text-[13px] tw-font-semibold tw-text-color-status-error-text">
                {{ "groceryRowMenuRemove" | i18n }}
              </span>
            </button>
          </div>
        }
      </span>
    } @else if (removable) {
      <!-- Small screen: tapping the row opens the row-actions bottom sheet. Keep an equivalent
           button for keyboard / assistive-tech users, visually hidden until focused. -->
      <button
        type="button"
        (click)="$event.stopPropagation(); openRowActionsSheet(row, removable)"
        class="tw-sr-only focus:tw-not-sr-only tw-flex tw-items-center tw-justify-center tw-p-1.5 tw-rounded-lg tw-bg-transparent tw-border-none tw-cursor-pointer hover:tw-bg-color-surface-elevated"
        [attr.aria-label]="'groceryRowMenuAria' | i18n"
        aria-haspopup="dialog"
      >
        <icon name="more-horizontal" [size]="4" currentColor="color-icon-secondary"></icon>
      </button>
    }
  </div>
</ng-template>

<!-- Search dropdown row: catalog match with store-price chips, a refresh action, and an "Add" pill. -->
<ng-template #searchRow let-row="row" let-isLast="isLast">
  <div
    [class]="clickableSearchRowClass"
    appKeyboardActivatable
    (appKeyboardActivatableActivate)="addItem(row.item)"
    (click)="addItem(row.item)"
  >
    <div class="tw-flex tw-flex-1 tw-flex-col tw-gap-1 tw-min-w-0">
      <p class="tw-truncate tw-text-[13.5px] tw-font-semibold tw-text-color-text-default tw-m-0">
        {{ row.item.name }}
      </p>
      <!-- Second row: unit price on the left, store chips on the right — so a product carried by
           every store never truncates its name. On phones a multi-store product's chips move to
           their own full-width line below (see the span after the refresh button). -->
      <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1.5 tw-w-full">
        <p
          class="tw-text-[11px] tw-font-medium tw-m-0 tw-whitespace-nowrap"
          [ngClass]="
            row.isBestValue ? 'tw-text-color-status-success-text' : 'tw-text-color-text-tertiary'
          "
        >
          {{ formatPrice(row.unitPrice.value) }}{{ row.unitPrice.label }}
          @if (row.isBestValue) {
            ·
            <span [attr.title]="searchVm().hasMore ? ('groceryBestValueCaveat' | i18n) : null">{{
              "groceryBestValue" | i18n
            }}</span>
          }
        </p>
        @if (!(isSmallScreen() && row.chips.length > 1)) {
          <span class="tw-flex tw-items-center tw-gap-1.5 tw-ml-auto tw-shrink-0">
            <ng-container
              *ngTemplateOutlet="chipList; context: { chips: row.chips }"
            ></ng-container>
          </span>
        }
      </div>
      <ng-container *ngTemplateOutlet="refreshMessage; context: { row: row }"></ng-container>
    </div>
    <button
      type="button"
      class="tw-border-none tw-bg-transparent tw-text-color-text-tertiary tw-cursor-pointer tw-p-1 tw-rounded hover:tw-bg-color-surface-elevated hover:tw-text-color-text-default disabled:tw-opacity-50 disabled:tw-cursor-default"
      [disabled]="refreshState()[row.item.id]?.loading"
      (click)="$event.stopPropagation(); refreshRow(row, false)"
      (keydown.enter)="$event.stopPropagation()"
      (keydown.space)="$event.stopPropagation()"
      [attr.aria-label]="'groceryRefreshPrices' | i18n"
      [attr.title]="'groceryRefreshPrices' | i18n"
    >
      <icon name="refresh-line" [size]="4" currentColor="color-icon-secondary"></icon>
    </button>
    <ng-container
      *ngTemplateOutlet="
        addPill;
        context: { ariaLabel: 'groceryAddItemAria' | i18n: row.item.name, item: row.item }
      "
    ></ng-container>
    @if (isSmallScreen() && row.chips.length > 1) {
      <span class="tw-flex tw-items-center tw-gap-1.5 tw-order-last tw-basis-full tw-flex-wrap">
        <ng-container *ngTemplateOutlet="chipList; context: { chips: row.chips }"></ng-container>
      </span>
    }
  </div>
  @if (!isLast) {
    <div class="tw-h-px tw-w-full tw-bg-color-border-subtle"></div>
  }
</ng-template>

<!-- Current-list row: the Archived Lists view's simplified item card (name, price chip, remove). -->
<ng-template #currentListRow let-row="row">
  <div
    class="tw-flex tw-flex-col tw-gap-2 tw-bg-color-surface-card tw-rounded-2xl tw-px-4 tw-py-3 tw-shadow-elevation-card"
    [attr.data-current-list-item-id]="row.listItemId"
    [ngClass]="
      highlightedListItemIds().has(row.listItemId)
        ? 'tw-outline tw-outline-2 tw-outline-color-status-success-border'
        : ''
    "
  >
    <div class="tw-flex tw-items-center tw-gap-3 tw-w-full">
      <span
        class="tw-text-sm tw-min-w-0 tw-truncate"
        [ngClass]="
          row.purchased
            ? 'tw-line-through tw-text-color-text-tertiary'
            : 'tw-text-color-text-default'
        "
      >
        {{ row.item.name }}
      </span>
      <span class="tw-flex-1"></span>
      <span class="tw-flex tw-items-center tw-gap-1.5 tw-shrink-0">
        @let chip = currentListChip(row);
        @if (row.isCustom) {
          <span
            class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-full tw-bg-color-status-warning-bg tw-text-color-status-warning-text tw-text-[13px] tw-font-medium tw-whitespace-nowrap"
          >
            {{ "groceryNoPriceYet" | i18n }}
          </span>
        } @else if (chip) {
          <span
            class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-full tw-text-[13px] tw-font-medium tw-whitespace-nowrap"
            [ngClass]="
              chip.isHighlighted
                ? 'tw-bg-color-status-success-bg tw-text-color-status-success-text'
                : 'tw-bg-color-surface-sunken tw-text-color-text-secondary'
            "
          >
            {{ chip.label }} {{ formatPrice(chip.price) }}
          </span>
        } @else {
          <span
            class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-min-w-[60px] tw-px-3 tw-rounded-full tw-bg-color-surface-sunken tw-text-color-text-tertiary tw-text-[13px] tw-font-medium tw-whitespace-nowrap"
          >
            {{ "groceryNoPriceYet" | i18n }}
          </span>
        }
        <button
          type="button"
          class="tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0.5"
          (click)="removeItem(row.listItemId)"
          [attr.aria-label]="'groceryRemoveItem' | i18n"
        >
          <icon name="close" [size]="4" currentColor="color-icon-secondary"></icon>
        </button>
      </span>
    </div>
    <!-- A 0 unit price means "no price" (see unitPrice) — custom/unpriced rows skip the line. -->
    @if (!row.isCustom && row.unitPrice.value > 0) {
      <p class="tw-text-xs tw-text-color-text-tertiary tw-m-0">
        {{ formatPrice(row.unitPrice.value) }}{{ row.unitPrice.label }}
      </p>
    }
  </div>
</ng-template>
